// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import Zone from "@insite/client-framework/Components/Zone";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import Modal, { ModalProps } from "@insite/mobius/Modal";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import FormActions from "@store/Library/Components/Toolkit/FormActions";
import constants from "@store/constants";
import { SendAlternatePackagingRequestApiParameter } from "@store/Framework/Services/AlternatePackagingService";
import clearForm from "@store/Framework/Store/Greif/Components/RecommendedProductRequestForm/Handlers/ClearForm";
import submitRecommendedProductRequestForm from "@store/Framework/Store/Greif/Components/RecommendedProductRequestForm/Handlers/SubmitRecommendedProductRequestForm";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { createRef, RefObject, useContext, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    submitButtonLabel = "submitButtonLabel",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.submitButtonLabel]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);

    return {
        session,
        pageTitle: getCurrentPage(state).fields.title,
        recommendedProductRequestForm: state.greif.components.recommendedProductRequestForm,
        shipTo: getShipToState(state, session.shipToId),
        billTo: getBillToState(state, session.billToId),
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<
        { location: string; recaptchaRef: React.RefObject<ReCAPTCHA> },
        boolean
    >(validateReCaptcha),
    clearForm,
    submitRecommendedProductRequestForm,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface RecommendedProductRequestFormStyles {
    gridContainer?: GridContainerProps;
    formGridItem?: GridItemProps;
    submitButton?: ButtonPresentationProps;
    modal?: ModalProps;
    modalContentWrapper?: InjectableCss;
}

export const recommendedProductRequestFormStyles: RecommendedProductRequestFormStyles = {
    gridContainer: {
        css: css`
            form {
                width: 100%;
            }
        `,
    },
    formGridItem: { width: 12 },
    modalContentWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(16)};
            flex-grow: 0;
        `,
    },
};

const styles = recommendedProductRequestFormStyles;

type Validator = () => boolean;

type ContextType = {
    validators: {
        [key: string]: Validator | undefined;
    };
};

export type FileData = {
    filename: string;
    data: string | ArrayBuffer | null | undefined;
};

export const RecommendedProductRequestFormContext = React.createContext<ContextType>({
    validators: {},
});

const RecommendedProductRequestForm = ({
    id,
    fields,
    session,
    location,
    history,
    recommendedProductRequestForm,
    shipTo,
    billTo,
    clearForm,
    submitRecommendedProductRequestForm,
}: Props) => {
    const { fieldValues } = recommendedProductRequestForm;
    const { submitButtonLabel } = fields;

    const [modalIsOpen, setModelIsOpen] = useState<boolean>(false);
    const toasterContext = useContext(ToasterContext);
    const recaptchaRef: RefObject<ReCAPTCHA | null> = createRef();

    const [validators] = useState<{ [key: string]: Validator | undefined }>({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const canSubmit = fieldValues["alternativeShortDescription"] && fieldValues["currentPackagingItem"];

    const queryString = location.search;
    const queryParams = parseQueryString<{ returnUrl: string }>(queryString.replace("?", ""));

    const modalOpenHandler = () => {
        setModelIsOpen(true);
    };

    const modalCloseHandler = () => {
        setModelIsOpen(false);
        history.push(queryParams.returnUrl);
        clearForm();
    };

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const widgetValidators: Validator[] = [];
        Object.keys(validators).forEach(key => {
            const validator = validators[key];
            if (validator) {
                widgetValidators.push(validator);
            }
        });

        const results = widgetValidators.map(v => v());

        if (!results.every(o => o)) {
            return;
        }

        const isReCaptchaValid = validateReCaptcha({
            location: "RecommendedProductRequest",
            recaptchaRef,
        });

        if (!isReCaptchaValid) {
            return;
        }

        const formData = {
            recommendedProductRequestModel: {
                billTo: billTo.value?.customerNumber,
                shipTo: shipTo.value?.customerSequence,
                websiteUserName: session.userName,
                alternativeType: fieldValues["alternativeType"],
                alternativeShortDescription: fieldValues["alternativeShortDescription"],
                currentPackagingItem: fieldValues["currentPackagingItem"],
                customerNumber: fieldValues["customerNumber"],
                customerCompany: fieldValues["customerCompany"],
                fullName: session.userLabel,
                firstName: fieldValues["firstName"],
                lastName: fieldValues["lastName"],
                phone: fieldValues["phone"],
                email: fieldValues["email"],
                requestMessage: fieldValues["requestMessage"],
                documents: fieldValues["attachedFiles"],
            },
        } as SendAlternatePackagingRequestApiParameter;

        setIsSubmitting(true);

        submitRecommendedProductRequestForm({
            ...formData,
            onSuccess: () => {
                setIsSubmitting(false);
                modalOpenHandler();
            },
            onError: () => {
                setIsSubmitting(false);
                toasterContext.addToast({
                    body: siteMessage("RecommendedProductRequest_FormSubmitError"),
                    messageType: "danger",
                    timeoutLength: 3000,
                });
            },
            onComplete() {
                this.onSuccess?.();
            },
        });
    };

    return (
        <>
            <GridContainer {...styles.gridContainer}>
                <GridItem {...styles.formGridItem}>
                    <RecommendedProductRequestFormContext.Provider value={{ validators }}>
                        <form id="RecommendedProductRequestForm" onSubmit={handleFormSubmit} noValidate>
                            <LoadingOverlay loading={isSubmitting}>
                                <Zone contentId={id} zoneName="Content" />
                            </LoadingOverlay>
                            <FormActions>
                                <Button {...styles.submitButton} type="submit" disabled={!canSubmit || isSubmitting}>
                                    {translate(submitButtonLabel || "")}
                                </Button>
                            </FormActions>
                        </form>
                    </RecommendedProductRequestFormContext.Provider>
                </GridItem>
            </GridContainer>
            <Modal
                {...styles.modal}
                headline={translate("Request Successfully Submitted")}
                isOpen={modalIsOpen}
                isCloseable={false}
            >
                <StyledWrapper {...styles.modalContentWrapper}>
                    <InfosBox type="success">{siteMessage("RecommendedProductRequest_FormSubmitSuccess")}</InfosBox>
                    <Button onClick={modalCloseHandler}>{translate("Go Back To The Product")}</Button>
                </StyledWrapper>
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withHistory(connect(mapStateToProps, mapDispatchToProps)(RecommendedProductRequestForm)),
    definition: {
        group: constants.widgetGroup.recommendedProductRequest,
        displayName: "Form",
        allowedContexts: [constants.pageType.recommendedProductRequest],
        fieldDefinitions: [
            {
                name: fields.submitButtonLabel,
                displayName: "Submit Button Label",
                editorTemplate: "TextField",
                defaultValue: "Send Request",
                fieldType: "Translatable",
            },
        ],
    },
};

export default widgetModule;
